import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { ReceiptVerification, ReceiptVerifyService } from '../../services/fixedAssetCount.service';
import { formatMoney } from '../../utils/format';

/** The verify URL carries a signed token: never leak it as a Referer. */
const useNoReferrer = () => {
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'referrer';
    m.content = 'no-referrer';
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1">{value}</Typography>
  </Box>
);

/** Public, unauthenticated receipt check: /verify/receipt/:id?t=token. Shows five facts, nothing else. */
export const ReceiptVerifyPage: React.FC = () => {
  useNoReferrer();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [data, setData] = useState<ReceiptVerification | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    if (!id || !token) { setFailed(true); return undefined; }
    ReceiptVerifyService.get(id, token)
      .then(d => { if (live) setData(d); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [id, token]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', p: 2, pt: 8, bgcolor: 'background.default' }}>
      <Paper sx={{ p: 3, width: '100%', maxWidth: 440 }}>
        {failed && (
          <Alert severity="error">
            This receipt could not be verified. The link may be damaged or the receipt may not exist.
          </Alert>
        )}
        {!failed && !data && <Box sx={{ textAlign: 'center' }}><CircularProgress /></Box>}
        {data && (
          <>
            <Alert severity={data.status === 'valid' ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {data.status === 'valid' ? 'This receipt is genuine.' : 'This receipt is genuine, but it has been reversed.'}
            </Alert>
            <Typography variant="overline">{data.companyName}</Typography>
            <Row label="Receipt number" value={data.receiptNumber} />
            <Row label="Amount" value={`${data.currency} ${formatMoney(data.amount)}`} />
            <Row label="Date" value={data.receiptDate} />
            <Row label="Status" value={data.status === 'valid' ? 'Valid' : 'Reversed'} />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ReceiptVerifyPage;
